import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Badge } from '@/shared/ui/badge';
import { Cloud, CloudRain, Sun, Wind, Droplets, AlertTriangle, CheckCircle } from 'lucide-react';
import { FieldData, WeatherForecast } from './types';
import { satelliteApi } from './satelliteApi';
import { cn } from '@/shared/lib/utils';

interface WeatherOverlayProps {
  field: FieldData;
}

export const WeatherOverlay: React.FC<WeatherOverlayProps> = ({ field }) => {
  const { t } = useTranslation();
  const [forecast, setForecast] = useState<WeatherForecast[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWeather();
  }, [field.id]);

  const fetchWeather = async () => {
    setLoading(true);
    try {
      const data = await satelliteApi.fetchWeatherForecast(field);
      setForecast(data);
    } catch (error) {
      console.error('Failed to fetch weather:', error);
    } finally {
      setLoading(false);
    }
  };

  const getWeatherIcon = (conditions: string) => {
    if (conditions.toLowerCase().includes('rain')) {
      return <CloudRain className="h-8 w-8 text-blue-500" />;
    }
    if (conditions.toLowerCase().includes('cloud')) {
      return <Cloud className="h-8 w-8 text-gray-500" />;
    }
    return <Sun className="h-8 w-8 text-yellow-500" />;
  };

  const activityLabels: Record<string, string> = {
    Fertilizing: t('satellite.weather.fertilizing', 'Fertilizing'),
    Spraying: t('satellite.weather.spraying', 'Spraying'),
    Planting: t('satellite.weather.planting', 'Planting'),
    Harvesting: t('satellite.weather.harvesting', 'Harvesting'),
  };

  const getBestActivityDays = () => {
    const activities = [
      { name: 'Fertilizing', ideal: (day: WeatherForecast) => day.precipitation === 0 && day.windSpeed < 10 },
      { name: 'Spraying', ideal: (day: WeatherForecast) => day.precipitation === 0 && day.windSpeed < 8 },
      { name: 'Planting', ideal: (day: WeatherForecast) => day.precipitation > 0 && day.precipitation < 5 },
      { name: 'Harvesting', ideal: (day: WeatherForecast) => day.precipitation === 0 && day.humidity < 70 },
    ];

    return activities.map((activity) => {
      const bestDays = forecast.filter((day) => activity.ideal(day));
      return {
        activity: activity.name,
        days: bestDays.length,
        nextDate: bestDays[0]?.date,
      };
    });
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  const activityRecommendations = getBestActivityDays();
  const hasAdvisories = forecast.some((day) => day.advisory);

  return (
    <div className="space-y-4">
      {/* Advisories */}
      {hasAdvisories && (
        <Alert className="border-yellow-500/20 bg-yellow-500/10">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-600">
            <div className="space-y-1">
              {forecast
                .filter((day) => day.advisory)
                .map((day, index) => (
                  <div key={index}>
                    <strong>{new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}:</strong>{' '}
                    {day.advisory}
                  </div>
                ))}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* 7-Day Forecast */}
      <Card>
        <CardHeader>
          <CardTitle>{t('satellite.weather.forecast', '7-Day Weather Forecast')}</CardTitle>
          <CardDescription>{t('satellite.weather.forecastDesc', 'Satellite weather data for {{name}}', { name: field.name })}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            {forecast.map((day, index) => (
              <div
                key={index}
                className={cn(
                  'p-3 rounded-lg border bg-card text-center space-y-2',
                  index === 0 && 'ring-2 ring-primary'
                )}
              >
                <div className="text-xs font-medium text-muted-foreground">
                  {index === 0
                    ? t('satellite.weather.today', 'Today')
                    : new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
                <div className="flex justify-center">{getWeatherIcon(day.conditions)}</div>
                <div className="text-sm font-semibold text-foreground">
                  {day.tempMax}° / {day.tempMin}°
                </div>
                {day.precipitation > 0 && (
                  <div className="flex items-center justify-center gap-1 text-xs text-blue-500">
                    <Droplets className="h-3 w-3" />
                    {day.precipitation}mm
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Activity Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle>{t('satellite.weather.bestDays', 'Best Days for Farm Activities')}</CardTitle>
          <CardDescription>{t('satellite.weather.bestDaysDesc', 'Based on weather conditions')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {activityRecommendations.map((rec, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
              >
                <div className="flex items-center gap-3">
                  {rec.days > 0 ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  )}
                  <div>
                    <div className="font-medium text-foreground">{activityLabels[rec.activity] || rec.activity}</div>
                    <div className="text-xs text-muted-foreground">
                      {rec.days > 0
                        ? t('satellite.weather.suitableDays', { count: rec.days, defaultValue: '{{count}} suitable days ahead' })
                        : t('satellite.weather.noIdeal', 'No ideal conditions this week')}
                    </div>
                  </div>
                </div>
                {rec.nextDate && (
                  <Badge variant="secondary">
                    {new Date(rec.nextDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Detailed Conditions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('satellite.weather.detailed', 'Detailed Conditions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {forecast.slice(0, 3).map((day, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 rounded-lg border bg-card"
              >
                <div className="flex-1">
                  <div className="font-medium text-sm text-foreground mb-1">
                    {index === 0
                      ? t('satellite.weather.today', 'Today')
                      : new Date(day.date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'short',
                          day: 'numeric',
                        })}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Droplets className="h-3 w-3" />
                      {day.humidity}%
                    </div>
                    <div className="flex items-center gap-1">
                      <Wind className="h-3 w-3" />
                      {day.windSpeed} km/h
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-foreground">
                    {day.tempMax}° / {day.tempMin}°
                  </div>
                  <div className="text-xs text-muted-foreground">{t(`satellite.weather.condition.${day.conditions.toLowerCase()}`, day.conditions)}</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
